import { IAlcolockRecord } from "@components/tacho/alcolockCompliance/alcolock.interfaces";
import { AlcolockService } from "@components/tacho/alcolockCompliance/alcolock.service";
import { IVeriTableHeader, IVeriTableRow, IVeriTableRowCell } from "@directives/_veri/veriTable/veriTable.component";
import { VeriTableService } from "@directives/_veri/veriTable/veriTable.service";
import { SortType } from "@enums/SortType";
import { AccountFeatures } from "@enums/account/AccountFeature";
import { IJourney } from "@interfaces/IJourney";
import { IVehicle } from "@interfaces/IVehicle";
import { AccountFeatureService } from "@services/accountFeature.service";
import { ColourService } from "@services/colour.service";
import { JourneyService } from "@services/journey.service";
import { ReasonCodeService } from "@services/reasonCode.service";
import { IDriverTachoHours, TachoService } from "@services/tacho.service";
import { VehicleService } from "@services/vehicle.service";
import { VeriDate } from "@services/veriDate.service";
import * as angular from "angular";
import * as moment from "moment";
import "./healthCheck.component.scss";

class ComponentController implements angular.IComponentController {

    loading = false;

    tableHeaders: IVeriTableHeader[] = [];
    tableRows: IVeriTableRow[] = [];

    vehicles: IVehicle[];
    tachoHours: IDriverTachoHours[];
    journeys: IJourney[];
    alcolockRecords: IAlcolockRecord[];

    static $inject = ["$q", "vehicleService", "journeyService", "alcolockService", "reasonCodeService", "tachoService", "veriTableService", "accountFeatureService", "veriDate", "colourService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly vehicleService: VehicleService,
        private readonly journeyService: JourneyService,
        private readonly alcolockService: AlcolockService,
        private readonly reasonCodeService: ReasonCodeService,
        private readonly tachoService: TachoService,
        private readonly veriTableService: VeriTableService,
        private readonly accountFeatureService: AccountFeatureService,
        private readonly veriDate: VeriDate,
        private readonly colourService: ColourService) {
    }

    $onInit() {
        this.init();
    }

    $onDestroy() {

    }

    // ---

    private init() {
        this.loading = true;
        this.vehicleService.getVehicleFeatureSets();

        const vehiclesPromise = this.vehicleService.getVehicles({ includeLatestPlots: true, includeLatestTemp: true, includeVOR: true, includePeripheralMappings: true });
        const tachoPromise = this.tachoService.getCurrentTachoForAllDrivers(true);
        const journeyPromise = this.journeyService.getAllJourneys(moment().add(-1, "day").toDate(), moment().toDate(), false);
        const alcolockPromise = this.alcolockService.getLatestAlcolockRecords();

        this.$q
            .all([vehiclesPromise, tachoPromise, journeyPromise, alcolockPromise])
            .then(([vehicles, tachoHours, journeys, alcolockRecords]) => {
                this.vehicles = vehicles;
                this.tachoHours = tachoHours;
                this.journeys = journeys;
                this.alcolockRecords = alcolockRecords;

                this.journeys.sort((a, b) => this.veriDate.sort(a.EndUtc, b.EndUtc));

                this.createTableHeaders();
                this.createTableRows();
            })
            .finally(() => {
                this.loading = false;
            });
    }

    private createTableHeaders() {
        this.tableHeaders = [
            this.veriTableService.createVehicleRegHeader(),
            { title: "GPS", sortable: true, sortType: SortType.Date, includeInSummary: true },
            { title: "Movement", sortable: true, sortType: SortType.Date, includeInSummary: true },
            { title: "Temperature", sortable: true, sortType: SortType.Date, includeInSummary: true, hide: !this.accountFeatureService.currentUserHasFeatureAccess(AccountFeatures.Temperature) },
            { title: "Tacho", sortable: true, sortType: SortType.Date, includeInSummary: true, hide: !this.accountFeatureService.currentUserHasFeatureAccess(AccountFeatures.Tacho) },
            { title: "Breathalock", sortable: true, sortType: SortType.Date, includeInSummary: true, hide: !this.accountFeatureService.currentUserHasFeatureAccess(AccountFeatures.Breathalock) },
        ];
    }

    private createTableRows() {
        this.tableRows = this.veriTableService.createTableRows(this.vehicles, v => this.createTableRow(v));
    }

    private createTableRow(videoEvent: IVehicle): IVeriTableRow<IVehicle> {
        const row: IVeriTableRow<IVehicle> = {
            cells: this.createTableRowCells(videoEvent),
            data: videoEvent,
            navPillFilterValues: [0],
        };

        return row;
    }

    private createTableRowCells(vehicle: IVehicle): IVeriTableRowCell[] {
        const twoHours = 2 * 60;
        const twelveHours = 12 * 60;
        const twentyFourHours = 24 * 60;

        var lastTempTime: moment.Moment = null;
        vehicle.Temperatures?.forEach(temp => {
            if (!lastTempTime || lastTempTime.isBefore(temp.Timestamp)) {
                lastTempTime = this.veriDate.createMoment(temp.Timestamp);
            }
        });

        var lastMovementTime: moment.Moment = null;
        if (vehicle.Plot) {
            if (this.reasonCodeService.isTravelling(vehicle.Plot?.Reason)) {
                lastMovementTime = vehicle.Plot.UtcMoment;
            }
            else {
                var vehicleJourneys = this.journeys.filter(v => v.VehicleId == vehicle.Id);
                if (vehicleJourneys.length) {
                    lastMovementTime = moment(vehicleJourneys[0].EndUtc);
                }
            }
        }

        var lastTachoRecord = this.tachoHours.find(t => t.Driver?.vehicle?.Id == vehicle.Id);
        var tachoMoment: moment.Moment = lastTachoRecord ? moment(lastTachoRecord.EventTime) : null;;

        var lastAlcolockRecord = this.alcolockRecords.find(r => r.VehicleId == vehicle.Id);
        var lastAlcolockRecordTime = lastAlcolockRecord ? moment(lastAlcolockRecord.DateTime) : null;

        return [
            this.veriTableService.createVehicleRegCell(vehicle),
            this.createTickboxDateContent(vehicle.Plot?.UtcMoment, twoHours, twelveHours),
            this.createTickboxDateContent(lastMovementTime, twoHours, twelveHours),
            this.createTickboxDateContent(lastTempTime, twoHours, twelveHours),
            this.createTickboxDateContent(tachoMoment, twoHours, twelveHours),
            this.createTickboxDateContent(lastAlcolockRecordTime, twoHours, twentyFourHours)
        ];
    }

    private createTickboxDateContent(time: moment.Moment, greenAge: number, amberAge: number): IVeriTableRowCell {
        const cell = this.veriTableService.createDateTimeCell(time);

        if (!time) {
            cell.excludeFromSummary = true;
            return cell;
        }

        const minutesDiff = moment().diff(time, "m");

        if (minutesDiff < greenAge) {
            cell.contents = `<i class="green-color material-icons" tooltip="${this.veriDate.formatDate(time, "timeago")}">done</i>`;
            cell.exportData = `Ok ${this.veriDate.formatDate(time, "timeago")}`;
            cell.excludeFromSummary = true;
        }
        else if (minutesDiff < amberAge) {
            cell.contents = `<i class="orange-color material-icons" tooltip="${this.veriDate.formatDate(time, "timeago")}">warning_amber</i>`;
            cell.exportData = `Warning ${this.veriDate.formatDate(time, "timeago")}`;

            cell.summaryLabel = "Warnings";
            cell.summarySubLabel = "Health Check";
            cell.summaryIcon = "warning_amber";
            cell.summaryColour = this.colourService.orangeColor;
        }
        else {
            cell.contents = `<i class="red-color material-icons" tooltip="${this.veriDate.formatDate(time, "timeago")}">warning</i>`;
            cell.exportData = `Not Working ${this.veriDate.formatDate(time, "timeago")}`;

            cell.summaryLabel = "Issues";
            cell.summarySubLabel = "Health Check";
            cell.summaryIcon = "warning";
            cell.summaryColour = this.colourService.redColor;
        }

        return cell;
    }
}

export class HealthCheckComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
        };

        this.controller = ComponentController;
        this.template = require("./healthCheck.component.html");
    }
}
